import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSearch } from "@fortawesome/free-solid-svg-icons";
import apiServices from "../../../../services/apiService";
import storageService from "../../../../utils/storageService";
import CommonTable from "../../../common/table/ComonTable";
import Breadcrumbs from "../../../common/BreadCrum/BreadCrum";
import PageHeadingSection from "../../../common/pageheading/PageHeadingSection";

const StaffList = () => {
    const navigate = useNavigate();
    const [staffList, setStaffList] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState(""); // '', 'Active', 'Inactive'
    const [selectedUnitId, setSelectedUnitId] = useState("");
    const [unitList, setUnitList] = useState([]);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const roleId = Number(storageService.getItem("roleId"));
    const employeeCode = storageService.getItem("employeeCode");

    // Role-based permissions (mirrors Angular logic exactly)
    const canAddStaff = roleId === 3 || roleId === 4;
    const canUseUnitFilter = roleId === 1 || roleId === 3;

    const fetchStaffList = async () => {
        setLoading(true);
        try {
            let currentSiteId = null;
            let currentUnitId = null;

            // Get user details to find siteId and unitId
            if (employeeCode) {
                const userRes = await apiServices({
                    url: '/ResidentialUser/GetResidentialUserById',
                    method: 'POST',
                    data: { userId: employeeCode }
                });
                if (userRes.success && userRes.data?.data?.[0]) {
                    currentSiteId = userRes.data.data[0].siteId;
                    currentUnitId = userRes.data.data[0].unitId;
                }
            }

            const response = await apiServices({
                method: "GET",
                url: `/ResidentialStaffMembers/GetAllResidentialStaffMembers`
            });

            if (response?.success && response?.data?.data) {
                let allStaff = response.data.data;

                // Role-based filtering - mirrors Angular switch statement
                let filtered = [];
                switch (roleId) {
                    case 1: // Super Admin - sees all
                        filtered = allStaff;
                        break;
                    case 3: // Site Admin - all staff (Angular commented out siteId filter)
                        filtered = allStaff;
                        break;
                    case 4: // Unit Admin - filter by siteId AND unitId
                        filtered = allStaff.filter(s => s.siteId === currentSiteId && s.unitId === currentUnitId);
                        break;
                    case 5: // Tenant - filter by siteId AND unitId
                        filtered = allStaff.filter(s => s.siteId === currentSiteId && s.unitId === currentUnitId);
                        break;
                    default:
                        filtered = [];
                }

                // Sort by createdOn descending (mirrors Angular applyFilters sort)
                filtered.sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime());

                setStaffList(filtered);

                // Generate unit list for filter dropdown (for roles 1 and 3)
                if (canUseUnitFilter) {
                    const units = [...new Set(filtered.map(s => s.unitId).filter(Boolean))];
                    setUnitList(units);
                }
            } else {
                toast.error(response?.message || "Failed to fetch staff members");
            }
        } catch (error) {
            console.error("Error fetching staff:", error);
            toast.error("Error fetching staff members");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStaffList();
    }, []);

    const handleEdit = (row) => {
        navigate(`/zentres/Staff-management/staff-Form/${row.mobileNumber}`);
    };

    const handleLimitChange = (newLimit) => {
        setPageSize(newLimit);
        setCurrentPage(1);
    };

    // Apply all filters (mirrors Angular applyFilters)
    const filteredStaff = useMemo(() => {
        return staffList.filter(s => {
            const matchesSearch = !searchTerm ||
                s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                s.mobileNumber?.toString().includes(searchTerm);

            const matchesStatus = !statusFilter ||
                (statusFilter === 'Active' && s.status === 'Active') ||
                (statusFilter === 'Inactive' && s.status === 'Inactive');

            const matchesUnit = !selectedUnitId || s.unitId == selectedUnitId;

            return matchesSearch && matchesStatus && matchesUnit;
        });
    }, [staffList, searchTerm, statusFilter, selectedUnitId]);

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredStaff.slice(start, start + pageSize);
    }, [filteredStaff, currentPage, pageSize]);

    const columns = [
        { key: "no", label: "No", align: "center" },
        { key: "name", label: "Name", align: "left" },
        { key: "role", label: "Role", align: "left" },
        { key: "image", label: "Photo", align: "center" },
        { key: "mobileNumber", label: "Mobile", align: "left" },
        { key: "idProof", label: "ID Proof", align: "center" },
        { key: "idProofType", label: "ID Type", align: "left" },
        { key: "unitName", label: "Flat", align: "left" },
        { key: "status", label: "Status", align: "center" },
        { key: "actions", label: "Actions", align: "center" },
    ];

    const renderCell = (key, row, index) => {
        switch (key) {
            case "no":
                return <span className="text-[13px] font-medium text-[#64748B]">{index + 1 + (currentPage - 1) * pageSize}</span>;
            case "name":
                return <span className="text-[13px] font-bold text-gray-800 tracking-tight">{row.name || "—"}</span>;
            case "role":
                return <span className="text-[13px] font-medium text-[#64748B]">{row.role || "—"}</span>;
            case "image":
                return (
                    <div className="flex justify-center">
                        <img
                            src={row.image || "/assets/image/Noimage.jpg"}
                            alt={row.name}
                            className="w-9 h-9 rounded-full object-cover border-2 border-gray-100 shadow-sm"
                            onError={(e) => { e.target.src = "https://ui-avatars.com/api/?name=" + encodeURIComponent(row.name || 'S') + "&background=E31F26&color=fff&size=36"; }}
                        />
                    </div>
                );
            case "mobileNumber":
                return <span className="text-[13px] font-medium text-[#64748B]">{row.mobileNumber || "—"}</span>;
            case "idProof":
                return row.idProof ? (
                    <div className="flex justify-center">
                        <img
                            src={row.idProof}
                            alt="ID Proof"
                            className="w-10 h-7 object-cover rounded border border-gray-200 shadow-sm"
                            onError={(e) => { e.target.style.display = 'none'; }}
                        />
                    </div>
                ) : <span className="text-[13px] text-gray-400">—</span>;
            case "idProofType":
                return <span className="text-[13px] font-medium text-[#64748B]">{row.idProofType || "—"}</span>;
            case "unitName":
                return (
                    <span className="text-[13px] font-medium text-[#64748B]">
                        {row.unitName ? `${row.unitName} (${row.unitId})` : "—"}
                    </span>
                );
            case "status":
                const status = row.status || 'Inactive';
                const statusColor = status === 'Active'
                    ? "bg-[#DCFCE7] text-[#166534]"
                    : "bg-[#FEF9C3] text-[#D97706]";
                return (
                    <span className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold shadow-sm ${statusColor}`}>
                        {status}
                    </span>
                );
            case "actions":
                return null;
            default:
                return <span className="text-[13px] font-medium text-[#64748B]">{row[key] || "—"}</span>;
        }
    };

    const headerAction = (
        <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative w-full sm:w-60">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <input
                    type="text"
                    placeholder="Search by name or mobile..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    className="w-full pl-9 pr-4 py-2 border border-gray-200 bg-[#F8FAFC] rounded-lg text-xs font-medium focus:outline-none focus:border-blue-500 focus:bg-white transition-colors"
                />
            </div>

            {/* Status filter tabs - mirrors Angular status-buttons */}
            <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                {["", "Active", "Inactive"].map((val) => (
                    <button
                        key={val || "all"}
                        onClick={() => { setStatusFilter(val); setCurrentPage(1); }}
                        className={`px-3 py-2 text-xs font-bold transition-all ${statusFilter === val
                            ? "bg-[#E31F26] text-white"
                            : "bg-white text-gray-500 hover:bg-gray-50"
                            }`}
                    >
                        {val || "All"}
                    </button>
                ))}
            </div>

            {/* Unit dropdown - only for roles 1 and 3 */}
            {canUseUnitFilter && unitList.length > 0 && (
                <select
                    value={selectedUnitId}
                    onChange={(e) => { setSelectedUnitId(e.target.value); setCurrentPage(1); }}
                    className="px-3 py-2 border border-gray-200 bg-[#F8FAFC] rounded-lg text-xs font-medium focus:outline-none focus:border-blue-500 transition-colors"
                >
                    <option value="">All Units</option>
                    {unitList.map(unit => (
                        <option key={unit} value={unit}>{unit}</option>
                    ))}
                </select>
            )}
        </div>
    );

    return (
        <div className="bg-white px-6 py-4 border flex flex-col rounded-lg flex-1 overflow-y-auto gap-6 pt-4 scrollbar-hide">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading="Staff Management"
                subHeading="Manage your residential staff members"
                primaryBtnText={canAddStaff ? "Add Staff" : undefined}
                primaryBtnIcon={canAddStaff ? faPlus : undefined}
                PrimaryOnClick={canAddStaff ? () => navigate('/zentres/Staff-management/staff-Form/add') : undefined}
            />

            <div className="shrink-0 flex-1 flex flex-col bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <CommonTable
                    headers={columns}
                    headerAction={headerAction}
                    data={paginatedData.map((d, i) => ({ ...d, originalIndex: i }))}
                    renderCell={(key, row) => renderCell(key, row, row.originalIndex)}
                    pagination={{
                        pageNumber: currentPage,
                        pageSize,
                        totalRecords: filteredStaff.length,
                        totalPages: Math.ceil(filteredStaff.length / pageSize) || 1
                    }}
                    onPageChange={(page) => setCurrentPage(page)}
                    onLimitChange={handleLimitChange}
                    loading={loading}
                    onEdit={canAddStaff ? handleEdit : undefined}
                />
            </div>
        </div>
    );
};

export default StaffList;
